import { ElementSource, type ElementData } from './element';
import { type ElementClipboardData } from './ui';

const emptyAdf = JSON.stringify({
  type: 'doc',
  version: 1,
  content: [{ type: 'paragraph', content: [] }],
});

export type Sanitizer = (
  element: ElementClipboardData,
  opts: SanitizeOptions
) => ElementClipboardData;

/**
 * Options that help identify which properties can / can't be stripped from
 * element data
 */
export type SanitizeOptions = {
  /**
   * Identifies whether the element content should be overwritten
   */
  retainContent?: boolean;

  /**
   * The author of the new content if the old content is being overwritten
   */
  createdBy?: string;

  /**
   * The author visibility state of the new content if the old content is being
   * overwritten
   */
  isAuthorVisible?: boolean;
};

/**
 * Strips properties from an element that do not need to be included in the copy payload.
 */
export function serializeCopyPayload(
  element: ElementData,
  opts: SanitizeOptions = { retainContent: true },
  sanitizers?: Sanitizer[]
): ElementClipboardData {
  const { retainContent, createdBy, isAuthorVisible } = opts;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, lockedByUser, ...payload } = element;
  const overrides: any = {};

  if (!retainContent && 'createdBy' in payload) {
    overrides.createdBy = createdBy;
  }

  if (!retainContent && 'isAuthorVisible' in payload) {
    overrides.isAuthorVisible = isAuthorVisible;
  }

  if (!retainContent && 'text' in payload) {
    overrides.text = emptyAdf;
  }

  let output = {
    ...payload,
    ...overrides,
    source: ElementSource.USER,
  } as ElementClipboardData;

  if (sanitizers) {
    for (const sanitizer of sanitizers) {
      output = sanitizer(output, opts);
    }
  }

  return output;
}
